import React, { useState } from 'react';
import IntlMessages from '@projectK/helpers/IntlMessages';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { useMobileSettings } from '@projectK/context/MobileContextProvider';
import { useToast } from '@projectK/context/ToastProvider';
import { SeparateMemberModalMain } from '@projectK/MainComponents';
import PropTypes from 'prop-types';
import * as yup from 'yup';

const validationSchema = yup.object({
  first_name: yup
    .string()
    .matches(/^\S(?:.*\S)?$/, () => <IntlMessages id='validation.firstName.noSpaces' />)
    .required(<IntlMessages id='validation.firstNameRequired' />),
  last_name: yup
    .string()
    .matches(/^\S(?:.*\S)?$/, () => <IntlMessages id='validation.lastName.noSpaces' />)
    .required(<IntlMessages id='validation.lastNameRequired' />),
  email: yup
    .string()
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
      () => <IntlMessages id='validation.invalidEmailFormat' />
    )
    .typeError(<IntlMessages id='validation.invalidEmail' />)
    .required(<IntlMessages id='validation.emailRequired' />),
  password: yup
    .string()
    .required(<IntlMessages id='validation.passwordRequired' />)
    .min(8, () => <IntlMessages id='validation.passwordTooShort' />)
    .matches(
      /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+{}[\]:;<>,.?~\\/-])/,
      () => <IntlMessages id='validation.passwordWeak' />
    ),
  confirmPassword: yup
    .string()
    .required(<IntlMessages id='validation.repeatPasswordRequired' />)
    .oneOf([yup.ref('password'), null], () => <IntlMessages id='validation.passwordsDoNotMatch' />),
});

const existingValidationSchema = yup.object({
  first_name: yup
    .string()
    .matches(/^\S(?:.*\S)?$/, () => <IntlMessages id='validation.firstName.noSpaces' />)
    .required(<IntlMessages id='validation.firstNameRequired' />),
  last_name: yup
    .string()
    .matches(/^\S(?:.*\S)?$/, () => <IntlMessages id='validation.firstName.noSpaces' />)
    .required(<IntlMessages id='validation.lastNameRequired' />),
  email: yup
    .string()
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
      () => <IntlMessages id='validation.invalidEmailFormat' />
    )
    .typeError(<IntlMessages id='validation.invalidEmail' />)
    .required(<IntlMessages id='validation.emailRequired' />),
});

const customScrollbarStyle = {
  // Define the styles for the scrollbar
  '&::-webkit-scrollbar': {
    width: '8px',
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#44444461', // Lighter black color
    borderRadius: '8px', // Curved border
  },

  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: '#666', // Lighter black color on hover
  },
};

const SeperateMembers = ({
  isOpen,
  onClose,
  fetchMembersData,
  data,
  parentId,
}) => {
  const companyTheme = CompanyTheme();
  const { isMobile, mobileMode } = useMobileSettings();
  const showToast = useToast();
  const infoViewActionsContext = useInfoViewActionsContext();
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showRetypeNewPassword, setShowRetypeNewPassword] =
    React.useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const { user } = useAuthUser();

  const onShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const onDownNewPassword = (event) => {
    event.preventDefault();
  };

  const onShowRetypeNewPassword = () => {
    setShowRetypeNewPassword(!showRetypeNewPassword);
  };

  const onDownRetypeNewPassword = (event) => {
    event.preventDefault();
  };
  const handleDialogOpen = () => {
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  const handleCloseModalDialog = () => {
    setOpenDialog(false); // to close the dialog
    handleModalClose(); //to close the modal
  };
  const handleModalClose = () => {
    onClose();
  };
  return (
    <SeparateMemberModalMain
      isOpen={isOpen}
      handleDialogOpen={handleDialogOpen}
      fetchMembersData={fetchMembersData}
      data={data}
      parentId={parentId}
      user={user}
      existingValidationSchema={existingValidationSchema}
      validationSchema={validationSchema}
      showToast={showToast}
      handleCloseModalDialog={handleCloseModalDialog}
      isMobile={isMobile}
      infoViewActionsContext={infoViewActionsContext}
      companyTheme={companyTheme}
      onShowNewPassword={onShowNewPassword}
      onDownNewPassword={onDownNewPassword}
      showNewPassword={showNewPassword}
      showRetypeNewPassword={showRetypeNewPassword}
      onShowRetypeNewPassword={onShowRetypeNewPassword}
      onDownRetypeNewPassword={onDownRetypeNewPassword}
      handleCloseDialog={handleCloseDialog}
      openDialog={openDialog}
      customScrollbarStyle={customScrollbarStyle}
      mobileMode={mobileMode}
      setShowRetypeNewPassword={setShowRetypeNewPassword}
      setShowNewPassword={setShowNewPassword}
      onClose={onClose}
    />
  );
};

export default SeperateMembers;

SeperateMembers.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  fetchMembersData: PropTypes.func,
  data: PropTypes.object,
  parentId: PropTypes.number,
};
